/**
 * Enhanced user behavior tracking service for Application Insights
 * Tracks view counts and time spent on specific steps/pages
 */

import { trackEvent } from "logger/logger";
import { InsightProps } from "logger/insights";

export enum TrackingStepName {
    GuestStep = "GuestStep",
    ChildrenStep = "ChildrenStep",
    DateStep = "DateStep",
    TimeStep = "TimeStep",
    ActivityStep = "ActivityStep",

    // Sub-steps
    ActivityItemList = "ActivityItemList",
    ActivityModal = "ActivityModal",
    ExperienceModal = "ExperienceModal",
    SummaryModal = "SummaryModal",
    DetailsPage = "DetailsPage",

    EditBooking = "EditBooking",
    EditBookingGuests = "EditBookingGuests",
    EditBookingTime = "EditBookingTime",
    EditBookingContact = "EditBookingContact",
    EditBookingMessage = "EditBookingMessage",

    // Booking request steps
    RequestGuestStep = "RequestGuestStep",
    RequestChildrenStep = "RequestChildrenStep",
    RequestDateStep = "RequestDateStep",
}

export interface StepMetadata {
    activityId?: string;
    activityName?: string;
    [key: string]: string | number | boolean | undefined;
}

interface TimerData {
    startTime: number;
    stepName: TrackingStepName;
    metadata?: StepMetadata;
}

class UserTrackingService {
    private timers: Map<string, TimerData> = new Map();
    private readonly STORAGE_KEY_PREFIX = "user_tracking_";

    /**
     * Get timer data for a specific step/page
     */
    getTimerData(stepName: TrackingStepName, metadata?: StepMetadata): TimerData | null {
        const timerId = this.getTimerId(stepName, metadata);
        return this.timers.get(timerId) || this.getTimerFromStorage(timerId);
    }

    /**
     * Start tracking time for a step/page
     */
    startTimer(stepName: TrackingStepName, metadata?: StepMetadata): void {
        const timerId = this.getTimerId(stepName, metadata);
        const startTime = Date.now();

        // Store in memory
        this.timers.set(timerId, {
            startTime,
            stepName,
            metadata,
        });

        // Persist to localStorage for resilience
        localStorage.setItem(
            `${this.STORAGE_KEY_PREFIX}${timerId}`,
            JSON.stringify({ startTime, stepName, metadata })
        );

        // Track view count event
        this.trackViewEvent(stepName, metadata);
    }

    /**
     * Stop tracking time and send duration event
     */
    stopTimer(stepName: TrackingStepName, metadata?: StepMetadata): number | null {
        const timerId = this.getTimerId(stepName, metadata);
        const timerData = this.timers.get(timerId) || this.getTimerFromStorage(timerId);

        if (!timerData) {
            console.warn(`No timer found for step: ${stepName}`, metadata);
            return null;
        }

        const duration = Date.now() - timerData.startTime;

        // Clean up
        this.timers.delete(timerId);
        localStorage.removeItem(`${this.STORAGE_KEY_PREFIX}${timerId}`);

        // Track duration event
        this.trackDurationEvent(stepName, duration, metadata);

        return duration;
    }

    /**
     * Track view count event
     */
    private trackViewEvent(stepName: TrackingStepName, metadata?: StepMetadata): void {
        const eventName = `Step.${stepName}.Viewed`;
        const properties: InsightProps = {
            stepName,
        };

        if (metadata) {
            Object.entries(metadata).forEach(([key, value]) => {
                if (value !== undefined) {
                    properties[key] = value;
                }
            });
        }

        trackEvent(eventName, properties);
    }

    /**
     * Track time spent event
     */
    private trackDurationEvent(
        stepName: TrackingStepName,
        duration: number,
        metadata?: StepMetadata
    ): void {
        const eventName = `Step.${stepName}.TimeSpent`;
        const properties: InsightProps = {
            stepName,
            durationMs: duration,
            durationSeconds: Math.round(duration / 1000),
        };

        if (metadata) {
            Object.entries(metadata).forEach(([key, value]) => {
                if (value !== undefined) {
                    properties[key] = value;
                }
            });
        }

        trackEvent(eventName, properties);
    }

    /**
     * Generate unique timer ID
     */
    private getTimerId(stepName: TrackingStepName, metadata?: StepMetadata): string {
        if (metadata?.activityId) {
            return `${stepName}_${metadata.activityId}`;
        }
        return stepName;
    }

    /**
     * Retrieve timer from localStorage
     */
    private getTimerFromStorage(timerId: string): TimerData | null {
        try {
            const stored = localStorage.getItem(`${this.STORAGE_KEY_PREFIX}${timerId}`);
            return stored ? (JSON.parse(stored) as TimerData) : null;
        } catch (error) {
            console.warn("Failed to parse timer from storage:", error);
            return null;
        }
    }

    /**
     * Clean up all active timers (e.g., on page unload)
     */
    cleanupAllTimers(): void {
        // Stop all active timers
        this.timers.forEach((timerData) => {
            const duration = Date.now() - timerData.startTime;
            this.trackDurationEvent(timerData.stepName, duration, timerData.metadata);
        });

        // Clear memory and storage
        this.timers.clear();
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }

    /**
     * Track a simple event without timing
     */
    trackSimpleEvent(eventName: string, properties?: InsightProps): void {
        trackEvent(eventName, properties);
    }
}

export const userTrackingService = new UserTrackingService();

// Cleanup on page unload
if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", () => {
        userTrackingService.cleanupAllTimers();
    });
}
